import React, { useState, useEffect } from "react";
import {
  X,
  MapPin,
  Tag,
  Map,
  DollarSign,
  Wallet,
  Globe,
  Calendar,
  CalendarDays,
  CheckCircle,
  XCircle,
  FileText,
  Info,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";

export default function BossUpdatePlacesModal({
  visible,
  onClose,
  place,
  onUpdateSuccess,
}) {
  const [placeName, setPlaceName] = useState("");
  const [profit, setProfit] = useState("");
  const [paidWay, setPaidWay] = useState(1); // 1=Monthly, 2=Daily, 3=Global
  const [placeDailySalary, setPlaceDailySalary] = useState("");
  const [active, setActive] = useState(1); // 1=Active, 2=Inactive
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  // Populate fields when place changes
  useEffect(() => {
    if (place) {
      setPlaceName(place.place || "");
      setProfit(place.profit?.toString() || "");
      setPaidWay(place.paidWay || 1);
      setPlaceDailySalary(place.placeDailySalary?.toString() || "");
      setActive(place.active || 1);
      setNote(place.note || "");
    }
  }, [place]);

  const handleUpdate = async () => {
    if (!placeName.trim()) {
      alert("Please enter place address");
      return;
    }

    if (paidWay === 1 || paidWay === 3) {
      if (!profit.trim() || isNaN(profit)) {
        alert("Please enter a valid profit");
        return;
      }
    } else if (paidWay === 2) {
      if (!placeDailySalary.trim() || isNaN(placeDailySalary)) {
        alert("Please enter a valid place daily salary");
        return;
      }
    }

    setLoading(true);
    try {
      const response = await axios.put(`${MyDomain}Places/UpdatePlace`, {
        Id: place.id,
        Place: placeName.trim(),
        Profit:
          (paidWay === 1 || paidWay === 3) && profit ? parseInt(profit) : 0,
        PlaceType: 1,
        PaidWay: paidWay,
        PlaceDailySalary:
          paidWay === 2 && placeDailySalary ? parseInt(placeDailySalary) : null,
        Active: active,
        Note: note.trim() || null,
      });

      // Update choosedPlace in localStorage
      const updated = response.data.allObject;
      if (updated) {
        localStorage.setItem("choosedPlace", JSON.stringify(updated));

        // Also patch it inside allData.myPlaces
        const currentData = JSON.parse(localStorage.getItem("allData") || "{}");
        if (currentData.myPlaces) {
          currentData.myPlaces = currentData.myPlaces.map((p) =>
            p.id === place.id ? updated : p,
          );
          localStorage.setItem("allData", JSON.stringify(currentData));
        }
      }

      alert(response.data.message || "Place updated successfully!");
      onUpdateSuccess();
      onClose();
    } catch (error) {
      console.error("❌ Error updating place:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to update place";
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!visible || !place) return null;

  const infoText =
    paidWay === 2
      ? "Daily payment: revenue is tracked per working day based on the daily rate."
      : paidWay === 3
        ? "Global payment: a one-time total profit is agreed upon for the entire project."
        : "Update address, type and monthly profit for this place.";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-orange-400" />
            <h2 className="text-xl font-bold text-gray-900">Update Place</h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* ── Scrollable form ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-5">
            {/* Place ID (read-only) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Place ID
              </label>
              <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3 border border-gray-200">
                <Tag className="w-5 h-5 text-gray-400" />
                <span className="text-gray-500 font-semibold">
                  #{place?.id}
                </span>
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Address
              </label>
              <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-orange-400 transition">
                <Map className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                <textarea
                  value={placeName}
                  onChange={(e) => setPlaceName(e.target.value)}
                  placeholder="Enter place address"
                  rows={2}
                  className="flex-1 bg-transparent outline-none text-gray-900 resize-none"
                />
              </div>
            </div>

            {/* Active Status */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Place Status
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setActive(1)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    active === 1
                      ? "bg-green-50 border-green-500 text-green-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Active</span>
                  {active === 1 && <CheckCircle className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setActive(2)}
                  className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition font-semibold ${
                    active === 2
                      ? "bg-red-50 border-red-500 text-red-700"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <XCircle className="w-5 h-5" />
                  <span>Inactive</span>
                  {active === 2 && <CheckCircle className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Payment Method
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 1, label: "Monthly", Icon: Calendar },
                  { value: 2, label: "Daily", Icon: CalendarDays },
                  { value: 3, label: "Global", Icon: Globe },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    onClick={() => {
                      setPaidWay(value);
                      if (value === 2) setProfit("");
                      else setPlaceDailySalary("");
                    }}
                    className={`flex items-center justify-center gap-1.5 py-3 px-2 rounded-xl border transition font-semibold text-sm ${
                      paidWay === value
                        ? "bg-orange-400 border-orange-400 text-white"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Profit — Monthly or Global */}
            {(paidWay === 1 || paidWay === 3) && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {paidWay === 3 ? "Global Profit" : "Monthly Profit"}
                </label>
                <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-green-400 transition">
                  <DollarSign className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <input
                    type="number"
                    value={profit}
                    onChange={(e) => setProfit(e.target.value)}
                    placeholder={
                      paidWay === 3
                        ? "Enter global profit"
                        : "Enter profit amount"
                    }
                    className="flex-1 bg-transparent outline-none text-gray-900"
                  />
                  <span className="text-gray-500 font-semibold">₪</span>
                </div>
              </div>
            )}

            {/* Place Daily Salary — Daily only */}
            {paidWay === 2 && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Place Daily Salary
                </label>
                <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 focus-within:border-purple-400 transition">
                  <Wallet className="w-5 h-5 text-purple-500 flex-shrink-0" />
                  <input
                    type="number"
                    value={placeDailySalary}
                    onChange={(e) => setPlaceDailySalary(e.target.value)}
                    placeholder="Enter place daily salary"
                    className="flex-1 bg-transparent outline-none text-gray-900"
                  />
                  <span className="text-purple-500 font-semibold">₪</span>
                </div>
                <p className="text-xs text-purple-500 font-medium mt-1.5">
                  Daily revenue collected from the place each working day.
                </p>
              </div>
            )}

            {/* Note */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Note (Optional)
              </label>
              <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-blue-400 transition">
                <FileText className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add notes or details"
                  rows={3}
                  maxLength={200}
                  className="flex-1 bg-transparent outline-none text-gray-900 resize-none"
                />
              </div>
              <p className="text-xs text-gray-400 text-right mt-1">
                {note.length}/200
              </p>
            </div>

            {/* Info card */}
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <Info className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-orange-800 leading-relaxed">
                {infoText}
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={loading}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition ${
              loading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-orange-400 hover:bg-orange-500 text-white"
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Update Place</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin   { animation: spin 1s linear infinite; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
